import {getTestProps} from '@benchling/js-test-utils';
import {StyleSheet, css} from 'aphrodite';
import React, {useMemo} from 'react';

import {SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES} from 'analysis-tool/AnalysisToolConstants';
import useMaybeDraftStep from 'analysis-tool/right-sidebar/components/useMaybeDraftStep';
import getDataFrameRenderOverrides from 'analysis-tool/ui/flag-rows/getDataFrameRenderOverrides';
import useRightSidebarItem from 'analysis-tool/ui/hooks/useRightSidebarItem';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import RegressionView from 'analysis-tool/ui/main-view/RegressionView';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getDefaultClientIdForView} from 'pipelines/PipelineViewUtils';
import {getLegacyStepFromAnalysis} from 'pipelines/StepUtil';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';

import getAdditionalMetadataContent from './analysis-adapter/getAdditionalMetadataContent';
import getAnalysisDataViewActions from './analysis-adapter/getAnalysisDataViewActions';
import handleOnTitleChange, {canEditTitle} from './analysis-adapter/handleOnTitleChange';
import {assertViewExists, getViewForViewReference} from './AnalysisViewUtils';
import getDataFrameColumnHeaderActions from './data-frame-view/getDataFrameColumnHeaderActions';
import DataView from './DataView';
import DataViewContext, {PreviewDraftStepData, rowSelectionState} from './DataViewContext';

const setChartPreviewsGlobalState = makeUseGlobalClientState<PreviewDraftStepData | null>(null);

export type AnalysisDataViewAdapterProps = MainViewComponentProps<AnalysisMainViewItemType.DATA_VIEW>;

export default function AnalysisDataViewAdapter({
  analysis,
  onChangeSelectedItem,
  onChangeSelectedRightSidebarItem,
  selectedItem: {viewReference},
  clientStateId,
  ...remainingMainViewComponentProps
}: AnalysisDataViewAdapterProps): JSX.Element {
  const view = getViewForViewReference(analysis, viewReference);
  assertViewExists(view, analysis, viewReference);
  const clientId = getDefaultClientIdForView(view);
  const [selectedRowIndices, _] = rowSelectionState.useGlobalClientState(clientId);

  const lastNode = view.lineage[view.lineage.length - 1];

  // We need previews to use a less specific client id because we're previewing against a step whose output
  // data changes through configuration changes.
  const viewStepId = view.lineage.length > 0 ? lastNode.step.id : 'EMPTY_ANALYSIS';
  const {draftStep} = useMaybeDraftStep(viewStepId, null);
  const [previewDraftStepData, setPreviewDraftStepData] =
    setChartPreviewsGlobalState.useGlobalClientState(viewStepId);

  const {selectedRightSidebarItem} = useRightSidebarItem(clientStateId);
  const contextValue = useMemo(() => {
    const lastNode = view.lineage[view.lineage.length - 1];
    const legacyLastStep = getLegacyStepFromAnalysis(analysis, lastNode.step);

    return {
      actionOrigin: ActionOrigin.ANALYSIS_TOOL,
      actions: getAnalysisDataViewActions(
        analysis,
        clientStateId,
        view,
        viewStepId,
        selectedRowIndices,
        onChangeSelectedItem,
        onChangeSelectedRightSidebarItem,
        selectedRightSidebarItem
      ),
      additionalMetadataContent: getAdditionalMetadataContent(analysis, view),
      analysisView: view,
      dataFrameViewOptions: {
        dataFrameRenderOverrides: getDataFrameRenderOverrides(analysis, legacyLastStep),
        headerMenuActions: getDataFrameColumnHeaderActions(analysis, clientStateId, view, {
          onChangeSelectedRightSidebarItem,
          selectedRightSidebarItem,
        }),
      },
      onTitleChange: canEditTitle(analysis, view)
        ? async (newTitle: string) => {
            await handleOnTitleChange(newTitle, analysis, view);
          }
        : undefined,
      previewData: draftStep
        ? {
            draftStep,
            previewDraftStepData: previewDraftStepData ?? {
              chartPreviews: [],
              previewStatus: AsyncStatus.NOT_FETCHED,
            },
            setPreviewDraftStepData,
          }
        : undefined,
    };
  }, [
    analysis,
    clientStateId,
    draftStep,
    onChangeSelectedItem,
    onChangeSelectedRightSidebarItem,
    previewDraftStepData,
    selectedRowIndices,
    setPreviewDraftStepData,
    view,
    viewStepId,
    selectedRightSidebarItem,
  ]);

  // For backwards compatibility with the regression functionality, since it hasn't been modernized to new views
  // yet, we special case in analyses to show `RegressionView` instead of `DataView`. We show this when it's
  // SUCCEEDED or WAITING (when the user hasn't configured the regression yet), and still default to `DataView`
  // for other step status. Before, this was all managed by the sidebar code.
  //
  // TODO(BNCH-135912): Remove regression special casing when regression is modernized to new views.
  if (
    lastNode.step.stepType === PipelineStepType.REGRESSION &&
    (lastNode.step.statusCode === PipelineStepStatusCode.SUCCEEDED ||
      lastNode.step.statusCode === PipelineStepStatusCode.WAITING ||
      lastNode.step.statusCode === PipelineStepStatusCode.AI_PROPOSED)
  ) {
    const legacyRegressionStep = getLegacyStepFromAnalysis(analysis, lastNode.step);
    return (
      <RegressionView
        {...remainingMainViewComponentProps}
        analysis={analysis}
        clientStateId={clientStateId}
        onChangeSelectedItem={onChangeSelectedItem}
        onChangeSelectedRightSidebarItem={onChangeSelectedRightSidebarItem}
        selectedItem={{type: AnalysisMainViewItemType.REGRESSION, stepId: lastNode.step.id}}
        step={legacyRegressionStep}
      />
    );
  }

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'AnalysisDataViewAdapter', element: 'root'})}
    >
      <DataViewContext.Provider value={contextValue}>
        <DataView />
      </DataViewContext.Provider>
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    ...SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES,
    height: SHARED_MAIN_VIEW_COMPONENT_WITH_MARGIN_STYLES.maxHeight,
  },
});
